/**
 * "Ny oppskrift" — the single entry point for creating a recipe (issue #3).
 * A bottom sheet on mobile and a centred dialog on desktop (CSS-only switch,
 * same `.dlg-overlay`/`.dlg-panel` pattern as `AddToCalendarDialog`).
 *
 * Options, in order: 1) Fra en nettside (highlighted; opens a paste-the-link
 * step inside the sheet), 2) Skriv selv (→ /recipes/new), 3) Fra bilde,
 * 4) Fra video, 5) Fra PDF. The import flows are the EXISTING components
 * (`UrlImportForm`, `PhotoImportButton`, `VideoImportButton`,
 * `PdfImportButton`) rendered as option rows — not forks — so their upfront
 * Premium gate (issue #19), polling and error handling are unchanged. A Free
 * cookbook gets a "Premium" badge on the three paid rows; tapping one shows
 * today's PremiumNotice right under the row.
 *
 * Shown only to writers (the caller gates it). UI mirror only — the server
 * stays authoritative.
 */

import type { RecipeImportDraft } from "@tjommi/shared";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useDialogA11y } from "../lib/useDialogA11y";
import type { PdfImportResult } from "../lib/importPoll";
import { PdfImportButton } from "./PdfImportButton";
import { PhotoImportButton } from "./PhotoImportButton";
import { UrlImportForm } from "./UrlImportForm";
import { VideoImportButton } from "./VideoImportButton";

const icon = (children: React.ReactNode): React.JSX.Element => (
  <svg
    width="21"
    height="21"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const LinkIcon = (): React.JSX.Element =>
  icon(
    <>
      <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
      <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
    </>,
  );
const PencilIcon = (): React.JSX.Element =>
  icon(
    <>
      <path d="M4 20l4-1 11-11-3-3L5 16z" />
      <path d="M14 6l3 3" />
    </>,
  );
const CameraIcon = (): React.JSX.Element =>
  icon(
    <>
      <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
      <circle cx="12" cy="13" r="3.5" />
    </>,
  );
const VideoIcon = (): React.JSX.Element =>
  icon(
    <>
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="M16 10l5-3v10l-5-3z" />
    </>,
  );
const FileIcon = (): React.JSX.Element =>
  icon(
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M9 13h6M9 17h6" />
    </>,
  );

/** The rich content of an option row (icon tile, title [+ Premium], blurb, chevron). */
function OptionBody({
  icon: ic,
  title,
  desc,
  premium,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  premium?: boolean;
}): React.JSX.Element {
  return (
    <>
      <span className="ny-oi">{ic}</span>
      <span className="ny-obody">
        <span className="ny-ot">
          {title}
          {premium && <span className="ny-prem">Premium</span>}
        </span>
        <span className="ny-od">{desc}</span>
      </span>
      <span className="ny-chev" aria-hidden="true">
        ›
      </span>
    </>
  );
}

export function NewRecipeSheet({
  cookbookId,
  isPremium,
  accessToken,
  restoreFocusRef,
  onClose,
  onSessionExpired,
  onImported,
  onPdfImported,
}: {
  cookbookId: string;
  /** The active COOKBOOK's Premium capability (owner's plan, §2.1). */
  isPremium: boolean;
  accessToken: string;
  /** The "Ny oppskrift" button — focus returns here on close. */
  restoreFocusRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  onSessionExpired: () => void;
  /** photo / url / video: an unsaved draft to review in the editor. */
  onImported: (draft: RecipeImportDraft) => void;
  /** pdf: recipes were persisted directly (no draft). */
  onPdfImported: (result: PdfImportResult) => void;
}): React.JSX.Element {
  const [step, setStep] = useState<"options" | "url">("options");
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const urlOptRef = useRef<HTMLButtonElement>(null);

  // An import in flight must not be thrown away by Escape or a backdrop click
  // (closing unmounts the import component and cancels its polling). Each
  // import component reports its busy state; focus can be on <body> while a
  // button is disabled, so the keydown target can't be relied on for this.
  const [busySources, setBusySources] = useState<Record<string, boolean>>({});
  const importBusy = Object.values(busySources).some(Boolean);
  const busyRef = useRef(false);
  busyRef.current = importBusy;
  const busy = useMemo(() => {
    const report = (key: string) => (b: boolean) =>
      setBusySources((prev) => ({ ...prev, [key]: b }));
    return {
      url: report("url"),
      photo: report("photo"),
      video: report("video"),
      pdf: report("pdf"),
    };
  }, []);
  const guardedClose = (): void => {
    if (!busyRef.current) onClose();
  };
  useDialogA11y({ panelRef, initialFocusRef: closeRef, restoreFocusRef, onClose: guardedClose });

  // Returning from the URL step must not drop focus to <body> (the focused
  // control just unmounted): land on the row that opened it.
  const prevStep = useRef(step);
  useEffect(() => {
    if (prevStep.current === "url" && step === "options") urlOptRef.current?.focus();
    prevStep.current = step;
  }, [step]);

  const title = step === "url" ? "Fra en nettside" : "Ny oppskrift";

  return (
    <div
      className="dlg-overlay"
      data-testid="new-recipe-overlay"
      // Only a click on the dim backdrop itself closes — clicks bubbling up
      // from the nested import modals (rendered inside this tree) must not.
      onClick={(e) => {
        if (e.target === e.currentTarget) guardedClose();
      }}
    >
      <div
        ref={panelRef}
        className="dlg-panel sheet-dialog"
        data-testid="new-recipe-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-recipe-title"
      >
        <div className="dlg-sheet-handle" aria-hidden="true" />
        {step === "url" && (
          <button
            type="button"
            className="sheet-back"
            data-testid="new-recipe-back"
            disabled={importBusy}
            onClick={() => setStep("options")}
          >
            ‹ Tilbake
          </button>
        )}
        <div className="sheet-h">
          <h2 id="new-recipe-title" className="sheet-title">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="sheet-x"
            aria-label="Lukk"
            data-testid="new-recipe-close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {step === "options" ? (
          <>
            <p className="sheet-sub">Hvordan vil du legge den inn?</p>
            <div className="ny-opts" data-testid="new-recipe-options">
              <button
                ref={urlOptRef}
                type="button"
                className="ny-opt is-main"
                data-testid="new-opt-url"
                onClick={() => setStep("url")}
              >
                <OptionBody
                  icon={<LinkIcon />}
                  title="Fra en nettside"
                  desc="Lim inn lenken, så henter vi ingredienser og fremgangsmåte."
                />
              </button>
              <Link to="/recipes/new" className="ny-opt" data-testid="new-opt-write">
                <OptionBody
                  icon={<PencilIcon />}
                  title="Skriv selv"
                  desc="Start med en tom oppskrift."
                />
              </Link>

              <div className="ny-divider">Fra filer og video</div>

              <PhotoImportButton
                cookbookId={cookbookId}
                isPremium={isPremium}
                accessToken={accessToken}
                onSessionExpired={onSessionExpired}
                onImported={onImported}
                onBusyChange={busy.photo}
                className="ny-opt"
                label={
                  <OptionBody
                    icon={<CameraIcon />}
                    title="Fra bilde"
                    desc="Ta bilde av en kokebokside eller en lapp."
                    premium={!isPremium}
                  />
                }
              />
              <VideoImportButton
                cookbookId={cookbookId}
                isPremium={isPremium}
                accessToken={accessToken}
                onSessionExpired={onSessionExpired}
                onImported={onImported}
                onBusyChange={busy.video}
                toggleClassName="ny-opt"
                toggleLabel={
                  <OptionBody
                    icon={<VideoIcon />}
                    title="Fra video"
                    desc="TikTok, Instagram eller YouTube."
                    premium={!isPremium}
                  />
                }
              />
              <PdfImportButton
                cookbookId={cookbookId}
                isPremium={isPremium}
                accessToken={accessToken}
                onSessionExpired={onSessionExpired}
                onImported={onPdfImported}
                onBusyChange={busy.pdf}
                toggleClassName="ny-opt"
                toggleLabel={
                  <OptionBody
                    icon={<FileIcon />}
                    title="Fra PDF"
                    desc="Hent mange oppskrifter på én gang."
                    premium={!isPremium}
                  />
                }
              />
            </div>
          </>
        ) : (
          <UrlImportForm
            cookbookId={cookbookId}
            accessToken={accessToken}
            onSessionExpired={onSessionExpired}
            onImported={onImported}
            onBusyChange={busy.url}
            defaultOpen
            onCancel={() => setStep("options")}
            inputLabel="Lenke til oppskriften"
            submitLabel="Hent oppskrift"
            hint="Du får se oppskriften og kan rette den før den lagres."
          />
        )}
      </div>
    </div>
  );
}
